// A chat turn whose reply streams in (lib/api.js streamChat), for
// pages/Luna.jsx and hooks/usePendingTurn.js — kept here so it can be tested
// on its own (streamedTurn.test.js).
import { sendChat, streamChat, accountStamp } from "./api";
import { makingGroup } from "./chatIntent";

export function randomId() {
  try {
    if (crypto?.randomUUID) return crypto.randomUUID();
  } catch (_) {}
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

// How long the /api/chat fallback below keeps asking while it gets 409 (the
// streamed request is still being answered). A spoken turn: ~9 s — the call
// is waiting in silence.
const SPOKEN_409 = { retries: 6, waitMs: 1500 };

// A typed turn: as long as the server can still be on it, counted from when
// the turn was sent (pollTurn's `since` — not from when its stream broke
// off). The server gives a turn serverMs (backend CHAT_ACTION_DEADLINE_S;
// the stream's file worker _FILE_WORKER_TIMEOUT_SECONDS) and lets the key go
// when that runs out — an ask under the key from then on would run, and
// bill, the whole turn again. So the last ask goes edgeMs before it (the
// worker starts a moment after the turn is sent, but an ask takes a moment
// to arrive too); past it only the thread is read (hooks/usePendingTurn.js:
// its late reads catch a reply kept right at the end). It used to be asked
// for 10 s past the deadline, and a 409 there was read as a claim left
// behind (health audit round 3, client stream). Asked again after 3 s, then
// a little later each time, at most every 15 s. A request that got no answer
// at all (a phone switching networks, a locked screen, nginx's own 503 while
// the API can't be reached) is asked again too — and while the server may
// never have had the turn (no 200 from its stream, no 409 yet), at most
// `unanswered` times in a row: offline from the start, the error shows after
// about a minute, not after five. A turn the server took (`accepted`) is
// asked for to the end whatever the connection does: it is being made, and
// sending it anew would make (and bill) it twice (audit client-01 / e2e-04).
export const TYPED_POLL = {
  serverMs: 300000, edgeMs: 2000, firstWaitMs: 3000, maxWaitMs: 15000, factor: 1.5, unanswered: 6,
};

// Until when a typed turn sent at `at` may be asked for under its key.
const askUntil = (at) => at + TYPED_POLL.serverMs - TYPED_POLL.edgeMs;

// nginx's own answer while the API can't be reached: a JSON 503 without the
// backend's `code` (deploy/nginx-lunai.tr.conf @luna_api_unavailable — its
// 502 and 504 become that). /api/chat never sends an uncoded 503 itself; its
// own 504 (the turn ran out of time, its key let go) is the turn's end, not
// such an answer.
function gatewayDown(e) {
  const r = e?.response;
  if (!r || r.status !== 503) return false;
  return !(r.data && typeof r.data === "object" && typeof r.data.code === "string" && r.data.code !== "");
}

// If the stream never got going — before any of the reply arrived: a
// network error, a server or proxy that can't stream — the turn is asked
// once the normal way, under the same idempotency key, so the server never
// answers it twice (a 409 means the streamed request is still being
// answered: its reply is replayed once it is done). A typed turn
// (recover: true) is asked that way too when the stream broke off on this
// side after part of the reply (the connection went, or it went quiet): the
// server carries on and keeps the reply under that key — the bubble then
// gets all of it instead of vanishing. (If the server had failed the turn
// itself, the key was let go and this asks it once more, like a retry.)
// Otherwise, after part of the reply arrived, a broken stream is a failed
// turn (the caller's catch). signal: the turn is let go — whatever is on its
// way then stops. onStatus: what the turn is making, once the server says
// (lib/api.js streamChat). onAccepted: the server took the turn (its stream
// answered 200) — kept with the turn, for a pick-up after a reload.
// onStillWorking, onConnection, since: see pollTurn. Resolves with what
// streamChat / sendChat resolve with: the reply text, or with args.full the
// whole turn (lib/api.js turnResult: reply, sources, attachments) — the same
// shape whichever way it came.
export async function sendStreamedTurn(args, {
  voice, idempotencyKey, signal = null, onDelta, recover = false, onStatus = null, onStep = null, onStillWorking = null,
  onConnection = null, since = null, onAccepted = null,
}) {
  let started = false;
  let accepted = false;
  try {
    return await streamChat({
      ...args,
      voice,
      idempotencyKey,
      signal,
      ...(onStatus ? { onStatus } : {}),
      ...(onStep ? { onStep } : {}),
      ...(onAccepted ? { onAccepted } : {}),
      onDelta: (text) => {
        started = true;
        onDelta(text);
      },
    });
  } catch (e) {
    if (signal?.aborted) throw e;
    if (!((!started && e?.canFallBack) || (recover && e?.resumable))) throw e;
    accepted = e?.accepted === true;
  }
  return pollTurn(args, { voice, idempotencyKey, signal, typed: recover, onStillWorking, onConnection, since, accepted });
}

// Asks /api/chat under the turn's key until it answers: the reply (replayed
// once the server has it), or a refusal (thrown as it came). Each 409 — the
// server still on it — calls onStillWorking() and is asked again: a spoken
// turn ~9 s, a typed one (typed: true) while TYPED_POLL allows from `since`
// (when the turn was sent; default now). accepted: the server took the turn
// (lib/api.js streamChat e.accepted) — requests with no answer at all then
// never end the wait early. onConnection(ok): false when a request got no
// answer at all (the waiting line then says it waits for the connection),
// true once one did. When it stops asking, the last error is thrown and says
// what the server was doing:
// - e.connectionLost: the turn's time ran out while the last requests got no
//   answer at all — the server may well have kept its reply (or file): it is
//   looked for once the connection is back, never asked anew;
// - e.stillWorking: it answered 409 within its own deadline (a spoken turn:
//   at all) — its file may still turn up, and asking anew would make (and
//   bill) it twice;
// - e.deadlinePassed: the turn's time was up before it could be asked.
// No flag: an ordinary failure — such as `unanswered` requests with no
// answer for a turn the server may never have had (offline from the start).
// So is another account signed in meanwhile (another tab of this browser):
// each ask reads the credentials afresh, and one under that account would
// run — and bill — the turn anew there (e.accountChanged; client review,
// round 2).
// Also how a typed turn left pending is picked up again after a reload
// (hooks/usePendingTurn.js).
export async function pollTurn(args, {
  voice = false, idempotencyKey, signal = null, typed = false, onStillWorking = null, onConnection = null, since = null,
  accepted = false,
}) {
  const start = Number.isFinite(since) ? since : Date.now();
  const deadline = askUntil(start);
  let wait = typed ? TYPED_POLL.firstWaitMs : SPOKEN_409.waitMs;
  let lastBusy = null; // when the latest 409 came
  let unanswered = 0;  // requests in a row that got no answer at all
  let last = null;
  const owner = accountStamp(); // the account the turn is asked for under
  const tell = (ok) => {
    try {
      onConnection?.(ok);
    } catch (_) {}
  };
  const giveUp = (e, atDeadline = false) => {
    if (lastBusy !== null) e.stillWorking = true;
    if (atDeadline && unanswered > 0) e.connectionLost = true;
    return e;
  };
  for (let attempt = 0; ; attempt++) {
    if (accountStamp() !== owner) {
      throw Object.assign(new Error("another account was signed in meanwhile"), { accountChanged: true });
    }
    if (typed && Date.now() >= deadline) {
      throw giveUp(last || Object.assign(new Error("the turn's time is up"), { deadlinePassed: true }), true);
    }
    try {
      const out = await sendChat({ ...args, voice, idempotencyKey, signal });
      tell(true);
      return out;
    } catch (e) {
      if (signal?.aborted) throw e;
      const busy = e?.response?.status === 409;
      const noAnswer = typed && ((!e?.response && e?.isAxiosError === true) || gatewayDown(e));
      if (e?.response && !noAnswer) tell(true);
      if (!busy && !noAnswer) throw e;
      if (busy) {
        lastBusy = Date.now();
        unanswered = 0;
        try {
          onStillWorking?.();
        } catch (_) {}
      } else {
        unanswered += 1;
        tell(false);
      }
      const done = typed
        ? !accepted && lastBusy === null && unanswered >= TYPED_POLL.unanswered
        : attempt >= SPOKEN_409.retries;
      if (done) throw giveUp(e);
      last = e;
      await new Promise((resolve) => setTimeout(resolve, typed ? Math.max(0, Math.min(wait, deadline - Date.now())) : wait));
      if (signal?.aborted) throw e;
      if (typed) wait = Math.min(TYPED_POLL.maxWaitMs, wait * TYPED_POLL.factor);
    }
  }
}

// What a typed turn's ended wait (pollTurn's error) leaves to say, for the
// turn making `kind` (lib/chatIntent.js: "pptx", "image", "doc"... or null):
// - "lost": its time ran out while the connection was gone: its reply — a
//   file, a picture, words — is looked for in its thread once it is back,
//   and asking again would make (and bill) it twice;
// - "promise": its file or picture is still being made — the server answered
//   409 within its own deadline: it turns up in the chat and in Ürettiklerim,
//   and asking again would make (and bill) it twice;
// - "maybe": the server had the turn but its answer never came — a text
//   reply, or its time up before it could be asked: it may have failed;
//   sending it again is offered;
// - null: an ordinary failure (the caller's own error).
export function giveUpOutcome(e, kind) {
  if (e?.connectionLost) return "lost";
  if (e?.stillWorking && makingGroup(kind)) return "promise";
  if (e?.stillWorking || e?.deadlinePassed) return "maybe";
  return null;
}

// Whether a typed turn sent at turn.at can still be asked for under its key
// (pollTurn); past that, only its thread is read.
export function pollable(turn, now = Date.now()) {
  return Number.isFinite(turn?.at) && now < askUntil(turn.at);
}

// ---- Which view a turn belongs to ----
// A turn is asked in one view — a mode and, when one is open, a Sohbet —
// and its reply is kept in that thread: it joins the screen only while that
// view is open (pages/Luna.jsx; otherwise a toast says where it is).
export function turnView(turn) {
  return { mode: turn?.mode === "work" ? "work" : "friend", conversationId: turn?.conversationId || null };
}

// The Sohbet a view shows, or null for the mode's own thread — which
// Sohbetlerim lists, and opens, under the mode's name ("friend" in Arkadaş
// Modu, "work" in LunaWorks — backend db.DEFAULT_THREADS): the same thread
// as no Sohbet at all.
export function viewThread(view) {
  const mode = view?.mode === "work" ? "work" : "friend";
  const id = view?.conversationId || null;
  return id === mode ? null : id;
}

export function sameView(a, b) {
  return Boolean(a && b) && (a.mode === "work" ? "work" : "friend") === (b.mode === "work" ? "work" : "friend")
    && viewThread(a) === viewThread(b);
}

// ---- A typed turn left waiting ----
// The typed turn being answered, kept in this tab's sessionStorage until
// it is answered or fails: { key, text, mode, conversationId, making, at,
// accepted, owner }. A reload, or the page back in view after its poll gave
// up (a phone locked mid-build), picks it up again under the same key
// (hooks/usePendingTurn.js): the reply the server kept under it, or 409
// while it is still being made — never a second build. accepted: the server
// took it (pollTurn's `accepted`). Older than PENDING_MAX_AGE_MS, it is let
// go — 20 minutes: a turn whose connection went is brought once it is back
// (its wait alone runs to the server's 300 s and its late reads 4 minutes
// past that; at 10 minutes it was gone half a minute later). owner: the
// account it was asked under (lib/api.js accountStamp) — a turn left by an
// account that signed out is never picked up under the next one (lib/api.js
// signOut also drops it).
const PENDING_KEY = "luna_pending_turn";
export const PENDING_MAX_AGE_MS = 20 * 60 * 1000;
// How far this device's clock may be off the server's when a turn is looked
// for in its thread (repliesInHistory's `since`, mergeTurnAnswer).
export const PENDING_CLOCK_SLACK_MS = 15 * 60 * 1000;

export function savePendingTurn(turn) {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ ...turn, owner: accountStamp() }));
  } catch (_) {} // (private mode: a reload just can't pick it up)
}

// key: only that turn's entry goes (a newer turn's stays).
export function clearPendingTurn(key = null) {
  try {
    if (key) {
      const kept = JSON.parse(sessionStorage.getItem(PENDING_KEY) || "null");
      if (!kept || kept.key !== key) return;
    }
    sessionStorage.removeItem(PENDING_KEY);
  } catch (_) {}
}

// The turn left waiting in this tab by the account signed in now (owner),
// or null (none, unreadable, too old, or another account's — those are let
// go).
export function loadPendingTurn(now = Date.now(), owner = accountStamp()) {
  let turn = null;
  try {
    turn = JSON.parse(sessionStorage.getItem(PENDING_KEY) || "null");
  } catch (_) {
    turn = null;
  }
  const fresh = turn && typeof turn === "object" && typeof turn.key === "string" && /^text-[\w-]{1,80}$/.test(turn.key)
    && typeof turn.text === "string" && turn.text.trim() !== ""
    && Number.isFinite(turn.at) && now - turn.at < PENDING_MAX_AGE_MS && turn.at - now < 60000
    && typeof owner === "string" && owner !== "" && turn.owner === owner;
  if (!fresh) {
    if (turn) clearPendingTurn();
    return null;
  }
  return {
    key: turn.key,
    text: turn.text,
    mode: turn.mode === "work" ? "work" : "friend",
    conversationId: typeof turn.conversationId === "string" && turn.conversationId ? turn.conversationId : null,
    making: typeof turn.making === "string" ? turn.making : null,
    at: turn.at,
    ...(turn.accepted === true ? { accepted: true } : {}),
  };
}

// Luna's messages answering `text` in a loaded thread (lib/api.js
// fetchMessages, oldest first): the ones after the latest user message with
// that text, up to the next user message — [] while nothing answers it
// yet; null when that message isn't there at all. since (ms): a message
// stamped earlier than that is an older ask of the same words, not this
// one (a generous margin covers the device's clock being off).
export function repliesInHistory(history, text, { since = null } = {}) {
  const want = typeof text === "string" ? text.trim() : "";
  if (!want || !Array.isArray(history)) return null;
  for (let i = history.length - 1; i >= 0; i--) {
    const m = history[i];
    if (m?.role !== "user" || typeof m.text !== "string" || m.text.trim() !== want) continue;
    const stamped = m.timestamp ? Date.parse(m.timestamp) : NaN;
    if (since !== null && Number.isFinite(stamped) && stamped < since) return null;
    const out = [];
    for (let j = i + 1; j < history.length && history[j]?.role !== "user"; j++) {
      if (history[j]?.role === "luna") out.push(history[j]);
    }
    return out;
  }
  return null;
}

// The ids a message on screen is known by: its own, and the server's id a
// live reply came back with (messageId) — a reloaded message's own id is
// the server's.
const knownIds = (m) => [m?.id, m?.messageId].filter((v) => v != null && v !== "").map(String);

// The screen with Luna's `answers` added at the end — those not on it
// already (by either id; see knownIds). The same list back when nothing is
// new.
export function addAnswers(messages, answers) {
  const list = Array.isArray(messages) ? messages : [];
  const seen = new Set(list.flatMap(knownIds));
  const fresh = [];
  for (const a of Array.isArray(answers) ? answers : []) {
    const ids = knownIds(a);
    if (ids.some((id) => seen.has(id))) continue;
    ids.forEach((id) => seen.add(id));
    fresh.push(a);
  }
  return fresh.length ? [...list, ...fresh] : list;
}

// Whether the screen shows `turn`'s own message: the bubble this tab put up
// for it (id u-<key>), or its stored copy in a reloaded thread — the same
// words, stamped no earlier than the turn (give or take the device's clock).
// An older ask of the same words is not it.
function asked(list, turn) {
  const want = typeof turn?.text === "string" ? turn.text.trim() : "";
  const own = `u-${turn?.key}`;
  const since = Number.isFinite(turn?.at) ? turn.at - PENDING_CLOCK_SLACK_MS : null;
  return list.some((x) => x?.id === own) || (since !== null && list.some((x) => {
    if (x?.role !== "user" || typeof x.text !== "string" || x.text.trim() !== want) return false;
    const stamped = x.timestamp ? Date.parse(x.timestamp) : NaN;
    return Number.isFinite(stamped) && stamped >= since;
  }));
}

// The screen (pages/Luna.jsx messages) with a typed turn's answer(s) added:
// the turn left waiting, picked up again (a reload, the page back in view, a
// late read of its thread), or a reply that finished after its view was left
// and opened again. Her answers already on screen are never added twice
// (addAnswers); when none is new, nothing changes. The turn's own bubble goes
// in before them unless the screen shows it (see `asked`) — it may not, after
// a reload or a view switch: the default thread opens on a greeting alone.
// With no answers yet, only that bubble (a poll is about to ask for them).
export function mergeTurnAnswer(messages, turn, answers = []) {
  const list = Array.isArray(messages) ? messages : [];
  const given = Array.isArray(answers) ? answers : [];
  const added = addAnswers(list, given);
  if (given.length && added === list) return list;
  if (asked(list, turn)) return added;
  const bubble = { id: `u-${turn.key}`, role: "user", text: turn.text, mode: turn.mode };
  return [...list, bubble, ...added.slice(list.length)];
}

// A typed turn's reply as it streams in (launch audit 2026-10-03: the chat
// showed nothing for 2-4 s, until the whole reply was written). The first
// bit opens Luna's bubble with streaming: true (FriendPanel then says
// "Yazıyor..." instead of showing the typing dots); later bits are added at
// most once per animation frame, so a fast stream doesn't re-render the list
// for every few characters. here(): whether the turn's view is still open —
// while it isn't, nothing joins the screen (the reply is kept in its own
// thread; pages/Luna.jsx says where). turn: the pending turn, for a reply
// whose bubble is gone when it finishes (see finish).
export function streamingReply(setMessages, mode, { here = null, turn = null } = {}) {
  const id = "l-" + Date.now();
  const onView = () => !here || Boolean(here());
  let shown = false;
  let text = "";
  let dirty = false;
  let frame = null;
  const flush = () => {
    frame = null;
    if (!dirty) return;
    dirty = false;
    const now = text;
    setMessages((m) => m.map((x) => (x.id === id ? { ...x, text: now } : x)));
  };
  const stop = () => {
    if (frame !== null) cancelAnimationFrame(frame);
    frame = null;
    dirty = false;
  };
  return {
    id,
    onDelta(piece) {
      text += piece;
      if (!shown) {
        if (!onView()) return; // (opened with all of it so far if the view comes back)
        shown = true;
        const first = text;
        setMessages((m) => [...m, { id, role: "luna", text: first, streaming: true, mode }]);
        return;
      }
      dirty = true;
      if (frame === null) frame = requestAnimationFrame(flush);
    },
    // The whole reply as the server stored it (and its help card) takes the
    // streamed text's place — or arrives in one go when nothing streamed
    // (the /api/chat fallback), or when the bubble is gone (its view was left
    // and opened again: the turn's own bubble comes back with it, see
    // mergeTurnAnswer). false when its view isn't open: nothing changed.
    finish(msg) {
      stop();
      if (!onView()) return false;
      setMessages((m) => (m.some((x) => x.id === id)
        ? m.map((x) => (x.id === id ? msg : x))
        : turn ? mergeTurnAnswer(m, turn, [msg]) : addAnswers(m, [msg])));
      return true;
    },
    // The turn failed after part of the reply showed (the server stopped
    // it, or it broke off and couldn't be fetched): the partial text goes —
    // it was never the whole answer.
    drop() {
      stop();
      if (shown) setMessages((m) => m.filter((x) => x.id !== id));
    },
  };
}
